import { Trans } from '@lingui/macro'
import { CheckOutlined, ClearOutlined } from '@mui/icons-material'
import { FilledInputProps, Grid, InputAdornment, InputProps, MenuItem, OutlinedInputProps, SxProps, TextField, TextFieldProps } from '@mui/material'
import { PortalLanguage } from 'app/backend/salesforce-maps/CommonTypes'

import { ReduxState, ReduxUser } from 'app/redux/ReduxInterfaces'
import { bilingualNumberFormat, getCurrentWords, PhoneCountryCodeFormat, PhoneFormat, PhoneFormatWithExtension } from 'app/views/common/Formats'
import { EMAIL_FORMAT_HELPER_TEXT, PHONE_NUMBER_FORMAT_HELPER_TEXT, URL_FORMAT_HELPER_TEXT } from 'app/views/forms/formTranslations'
import { Field, useField } from 'formik'
import { isEqual, isNil, isNumber } from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import NumberFormat from 'react-number-format'
import { useSelector } from 'react-redux'
import { isFalsy, valueExistsTS } from 'utilsTS'
import { countHelperText } from '../FormElement'
import { ErrorLabel } from '../forms/ErrorLabel'
import { FormTopLabel } from '../forms/FormTopLabel'

interface TextFieldWithFormikProps {
  maxWords?: number
  maxInput?: number
  minInput?: number
  limit?: number
  required?: boolean
  currency?: boolean
  type?: 'text' | 'number' | 'currency' | 'countryCode' | 'phone' | 'phoneWithExtension' | 'url' | 'email'
  language?: PortalLanguage
  scale?: number
  /**
   * This is an id of Formiks field that the TextField is connected to
   */
  name: string
  isTopLabel?: boolean // If true, label will be on top of the input, otherwise it will be inside the input
  innerRef?: React.RefObject<HTMLInputElement>
  showErrorWithoutTouch?: boolean
  helperArray?: Array<string | JSX.Element>
  actionIconButton?: React.ReactNode
  wordCount?: boolean
  multiline?: boolean
  topLabel?: React.ReactNode
  topLabelSx?: SxProps
  initialValue?: String
  isTopLabelSmall?: boolean
  decimalScale?: number
  sx?: SxProps
  instantUpdate?: boolean
  multiSelect?: boolean
}

/**
 * Convinience wrapper for Material UI Text Field connected to Formik
 * @category Common components
 * @subcategory Formik
 * @component
 * @returns {JSX.Element}
 * @property {string} name This is an id of Formiks field that the TextField is connected to
 */
export const TextFieldWithFormik = (
  {
    name,
    type = 'text',
    limit,
    topLabel,
    topLabelSx,
    required = false,
    maxInput,
    minInput,
    maxWords,
    disabled = false,
    isTopLabel,
    innerRef,
    select = false,
    wordCount = false,
    variant = 'outlined',
    showErrorWithoutTouch = false,
    actionIconButton,
    children,
    multiline = false,
    initialValue = '',
    isTopLabelSmall = true,
    decimalScale = 2,
    scale,
    sx,
    helperArray: helperArrayProp,
    instantUpdate = false,
    multiSelect = false,
    ...props
  }: TextFieldProps & TextFieldWithFormikProps & TextFieldWithFormikProps
): JSX.Element => {
  const isSelect = select || multiSelect
  const isInstantUpdate = instantUpdate || multiSelect
  const multiSelectProps = multiSelect
    ? {
      multiple: true,
      displayEmpty: true,
      renderValue: () =>
        <span style={{ fontWeight: 400 }}><Trans>MULTI_SELECT</Trans></span>
    }
    : {}

  const user = useSelector<ReduxState, ReduxUser>(state => state.user)
  const [field, meta, helpers] = useField(name)
  const isNumberField = type === 'number' || type === 'currency'
  const isValid =
    field.value === 0 ||
    (typeof field.value === 'string' && field.value !== '') ||
    (typeof field.value === 'number' && !Number.isNaN(field.value)) || // Accept valid field values: keep 0, non-empty strings, and valid numbers (not NaN)
    (Array.isArray(field.value) && multiSelect)

  const fieldValue = isValid ? field.value : initialValue // Fallback to initialValue only if field.value is null, undefined, '', false, or NaN

  const [inputValue, setInputValue] = useState(fieldValue)
  const [defaultZeroRemoved, setDefaultZeroRemoved] = useState(false)
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null)
  const [startEditingValue, setStartEditingValue] = useState(null)
  const helperArray = Array.isArray(helperArrayProp) ? [...helperArrayProp] : []

  const language = props.language ?? user.language

  const { touched = false } = meta

  const inputChanged = (e, validateOnKeystroke): void => {
    let value = e.target.value
    if (isEqual(value, inputValue)) {
      return
    }
    let invalid = false
    if (isNumber(maxWords)) {
      const words = +getCurrentWords(e.target.value)
      if (words > maxWords) {
        invalid = true
      } else if (words === maxWords) {
        value = value.trim()
      }
    }
    if (!invalid) {
      setInputValue(value)
    } else {
      setInputValue(inputValue)
    }
    if (isInstantUpdate) {
      void helpers.setValue(value, validateOnKeystroke)
      return
    }
    if (timer != null) {
      clearTimeout(timer)
    }
    const newTimer = setTimeout(() => {
      void helpers.setValue(value, validateOnKeystroke)
    }, 700)
    setTimer(newTimer)
  }

  useEffect(() => {
    setInputValue(fieldValue)
  }, [fieldValue])

  const NumberFormatWithMinAndMax = useCallback(
    (props) => {
      const { inputRef, id, maxLength, value = '', onChange, ...other } = props

      let separator = ','
      if (language !== 'en' && type === 'currency') {
        separator = ' '
      }
      let decimalScale
      if (!isNil(scale)) {
        decimalScale = scale
      } else if (type === 'currency') {
        decimalScale = 2
      }
      return (
        <NumberFormat
          {...other}
          isAllowed={({ floatValue, value }) => {
            if (!isNumber(floatValue)) {
              return true
            }

            if (valueExistsTS(maxInput)) {
              return floatValue <= Number(maxInput)
            }
            return true
          }}
          value={typeof props.value === 'object' ? '' : value}
          defaultValue=''
          thousandSeparator={separator}
          isNumericString={false}
          getInputRef={inputRef}
          allowNegative={false}
          decimalScale={decimalScale}
          suffix={language !== 'en' && type === 'currency' ? ' $' : ''}
          prefix={language === 'en' && type === 'currency' ? '$' : ''}
          allowLeadingZeros={false}
          onValueChange={(values) => {
            onChange({
              target: {
                name: props.name,
                value: values.value
              }
            })
          }}
        />
      )
    },
    [maxInput, minInput, type, language]
  )

  const baseProps: Partial<FilledInputProps> | Partial<OutlinedInputProps> | Partial<InputProps> = {}

  if (isNumberField) {
    baseProps.inputComponent = NumberFormatWithMinAndMax
  } else if (type.includes('phone')) {
    baseProps.inputComponent = type === 'phoneWithExtension'
      ? PhoneFormatWithExtension
      : PhoneFormat
    helperArray.push(
      <span>
        {PHONE_NUMBER_FORMAT_HELPER_TEXT}
        .
      </span>)
  } else if (type === 'countryCode') {
    baseProps.inputComponent = PhoneCountryCodeFormat
    limit = 5
  } else if (type === 'url') {
    helperArray.push(
      <span>
        {URL_FORMAT_HELPER_TEXT}
        .
      </span>)
  } else if (type === 'email') {
    helperArray.push(
      <span>
        {EMAIL_FORMAT_HELPER_TEXT}
        .
      </span>)
  }

  let stringValue = String(inputValue) ?? ''
  if (valueExistsTS(wordCount) || valueExistsTS(maxWords)) {
    helperArray.push(
      <span key='wordsCount'>
        {getCurrentWords(stringValue, maxWords)} <Trans>words</Trans>
        {'. '}
      </span>
    )
  }

  if (isNumberField) {
    stringValue = stringValue.replace(/[^0-9]/g, '')

    helperArray.push(
      <span key='numPerm'>
        <Trans>
          Only numerical values are permitted in this field
        </Trans>
      </span>
    )

    if (valueExistsTS(minInput)) {
      helperArray.push(
        <span key={1}>. </span>,
        <Trans>
          Minimum value is: {bilingualNumberFormat(minInput, language)}
        </Trans>
      )
    }
    if (valueExistsTS(maxInput)) {
      helperArray.push(
        <span key={1}>. </span>,
        <Trans>
          Maximum value is: {bilingualNumberFormat(maxInput, language)}
        </Trans>
      )
    }
  }

  // else {
  //   if (required) {
  //     helperArray.push(
  //       <span key='required'>
  //         <Trans>Required</Trans>
  //         {'. '}
  //       </span>
  //     )
  //   }
  // }

  let marginBottom: number | string = 0
  const style: React.CSSProperties = props.style ?? { marginTop: 5 }
  if (type === 'countryCode') {
    style.width = 92
  }
  if (valueExistsTS(style.marginBottom)) {
    marginBottom = style.marginBottom ?? style.margin ?? 0
    style.marginBottom = marginBottom
  }

  return (
    <Field name={name}>
      {({ field, meta, form }) => {
        const displayError = Boolean((touched || showErrorWithoutTouch) && meta.error)
        let inputError = false

        if (isNumber(maxInput) && !isFalsy(inputValue)) {
          if (maxInput < +inputValue) {
            inputError = true
          }
        }
        if (isNumber(minInput) && !isFalsy(inputValue)) {
          if (+minInput > +inputValue) {
            inputError = true
          }
        }
        if (isNumber(limit)) {
          if (isNumberField) {
            const onlyDigits = stringValue.replace(/[^0-9]/g, '')
            if (+limit < onlyDigits.length) {
              inputError = true
            }
          } else {
            if (+limit < stringValue.length) {
              inputError = true
            }
          }
        }

        let valueToShow = inputValue
        if (isFalsy(valueToShow) && valueToShow !== 0 && valueToShow !== '0') {
          valueToShow = props.defaultValue ?? ''
        }
        const showError = Boolean((meta.error != null && (touched || showErrorWithoutTouch)) || inputError)

        const toReturn = (
          <>
            {topLabel != null &&
              <FormTopLabel size={isTopLabelSmall ? 'small' : undefined} required={required}>
                {topLabel}
              </FormTopLabel>}
            <TextField
              name={name}
              value={valueToShow}
              onChange={e => {
                if (props.onChange != null) {
                  props.onChange(e)
                } else {
                  inputChanged(e, touched)
                }
              }}
              sx={[
                {
                  '& .MuiSelect-select.MuiSelect-outlined.MuiInputBase-input.MuiOutlinedInput-input': {
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center'
                  },
                  '& .MuiSelect-icon': {
                    fontSize: '24px'
                  }
                },
                ...(isNil(sx) ? [] : (Array.isArray(sx) ? sx : [sx]))
              ]}
              error={showError}
              InputProps={{
                ...baseProps,
                endAdornment: !disabled && (touched || Boolean(valueToShow))
                  ? (
                    <InputAdornment position='end'>
                      {!showError
                        ? (valueToShow != null && valueToShow !== '' &&
                          <CheckOutlined
                            sx={{
                              fontWeight: 700,
                              fontSize: '24px',
                              color: 'success.main',
                              marginRight: isSelect ? '15px' : 'initial'
                            }}
                          />
                        )
                        : (
                          <ClearOutlined
                            sx={{
                              color: 'error.main',
                              fontWeight: 700,
                              fontSize: '24px',
                              marginRight: isSelect ? '15px' : 'initial'
                            }}
                          />
                        )}
                    </InputAdornment>
                  )
                  : (
                    ''
                  )
              }}
              inputProps={{
                maxLength: isNumber(limit) && limit
              }}
              select={isSelect}
              SelectProps={{ ...multiSelectProps, ...props.SelectProps }}
              variant={variant}
              fullWidth
              FormHelperTextProps={{ component: 'div' }}
              helperText={
                <Grid container>
                  <Grid item xs>
                    {Boolean(displayError) && !disabled
                      ? <ErrorLabel label={meta.error} />
                      : disabled ? null : helperArray}
                  </Grid>
                  {isNumber(limit) && type !== 'countryCode' && !disabled &&
                    <Grid item sx={{ color: 'black !important', textAlign: 'right' }}>
                      {countHelperText(stringValue, Number(limit))}
                    </Grid>}
                </Grid>
              }
              type='text'
              onFocus={e => {
                if (startEditingValue === null) {
                  setStartEditingValue(inputValue)
                }
                if (type === 'number' && (valueToShow === 0 || valueToShow === '0')) {
                  setDefaultZeroRemoved(true)
                  setInputValue('')
                }
              }}
              onBlur={e => {
                if (timer != null) {
                  clearTimeout(timer)
                }
                if (!touched) {
                  form.setFieldTouched(name)
                }
                void helpers.setValue(inputValue)
                setStartEditingValue(null)
                if (type === 'number' && isFalsy(inputValue) && defaultZeroRemoved) {
                  setDefaultZeroRemoved(false)
                  setInputValue('0')
                } else if (defaultZeroRemoved) {
                  setDefaultZeroRemoved(false)
                }
                e.stopPropagation()
                e.preventDefault()
              }}
              disabled={disabled}
              {...props}
              multiline={multiline}
              style={{ wordBreak: 'break-word', ...style }}
            >
              {isSelect &&
                <MenuItem disabled value=''>
                  <span style={{ fontWeight: 400 }}>
                    {multiSelect ? <Trans>MULTI_SELECT</Trans> : <Trans>SELECT_ONE</Trans>}
                  </span>
                </MenuItem>}
              {children}
            </TextField>
          </>
        )

        if (actionIconButton != null) {
          return (
            <Grid
              container
              direction='row'
              justifyContent='center'
              alignItems='flex-start'
              gap='8px'
            >
              <Grid item xs>
                {toReturn}
              </Grid>
              <Grid item sx={{ paddingTop: '8px' }}>
                {actionIconButton}
              </Grid>
            </Grid>
          )
        } else {
          return toReturn
        }
      }}
    </Field>
  )
}
